// frontend/src/features/onboarding/OnboardingFlow.tsx
import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useAuth } from '@/components/providers/auth-provider'
import { settings, users, sources } from '@/lib/api'
import { ArrowRight, ArrowLeft } from 'lucide-react'

import { WelcomeLocationStep } from './steps/WelcomeLocationStep'
import { SourceSelectionStep } from './steps/SourceSelectionStep'
import { PolariStep } from './steps/PolariStep'
import { EchoStyleStep } from './steps/EchoStyleStep'
import { DeliveryScheduleStep } from './steps/DeliveryScheduleStep'

interface OnboardingData {
    geographic: { country: string; state: string; city: string }
    sources: number[]
    content: {
        briefing_style: string
        template_style: string
        format_style: string
        prose_paragraph_length: string
        enable_story_integration: boolean
    }
    delivery: {
        morning: { enabled: boolean; time: string }
        afternoon: { enabled: boolean; time: string }
        evening: { enabled: boolean; time: string }
    }
    timezone?: string
    auto_send_enabled?: boolean
    email_delivery_enabled?: boolean
}

// ── Step metadata ─────────────────────────────────────────────────────────────

const STEPS = [
    { label: 'Location', eyebrow: 'Step 1 of 5', accentClass: 'ob-eyebrow--default', btnClass: 'ob-btn-next--default' },
    { label: 'Sources', eyebrow: 'Step 2 of 5', accentClass: 'ob-eyebrow--default', btnClass: 'ob-btn-next--default' },
    { label: 'Polari', eyebrow: 'Step 3 of 5 · Polari', accentClass: 'ob-eyebrow--polari', btnClass: 'ob-btn-next--polari' },
    { label: 'Echo+Style', eyebrow: 'Step 4 of 5 · Echo', accentClass: 'ob-eyebrow--echo', btnClass: 'ob-btn-next--echo' },
    { label: 'Delivery', eyebrow: 'Step 5 of 5', accentClass: 'ob-eyebrow--default', btnClass: 'ob-btn-next--default' },
] as const

const TOTAL = STEPS.length

// ── Dot strip ─────────────────────────────────────────────────────────────────

function DotStrip({ current }: { current: number }) {
    return (
        <div className="ob-dots">
            {STEPS.map((_, i) => {
                let cls = 'ob-dot'
                if (i + 1 < current) cls += ' ob-dot--done'
                else if (i + 1 === current) cls += ' ob-dot--cur'
                return <div key={i} className={cls} />
            })}
        </div>
    )
}

// ── Main flow ─────────────────────────────────────────────────────────────────

export function OnboardingFlow() {
    const navigate = useNavigate()
    const { refreshUser } = useAuth()
    const [step, setStep] = useState(1)
    const [data, setData] = useState<Partial<OnboardingData>>({})
    const [waitingForVerification, setWaitingForVerification] = useState(false)

    const saveSettingsMutation = useMutation({
        mutationFn: async (settingsData: any) => settings.update(settingsData),
        onError: () => toast.error('Failed to save settings'),
    })

    const checkVerificationMutation = useMutation({
        mutationFn: async () => users.checkEmailVerified(),
        onError: () => toast.error('Could not verify email status'),
    })

    const requestVerificationMutation = useMutation({
        mutationFn: async () => users.resendVerificationEmail(),
        onSuccess: () => toast.info('Verification email sent.'),
        onError: () => toast.error('Could not send verification email'),
    })

    const updateData = (stepData: Partial<OnboardingData>) =>
        setData(prev => ({ ...prev, ...stepData }))

    const handleCompleteWizard = async () => {
        try {
            await users.completeOnboarding()
            await refreshUser()
            const isVerified = await checkVerificationMutation.mutateAsync()
            toast.success('Welcome to Digestr!')
            if (isVerified) {
                await handleEmailVerifiedAndFinish()
            } else {
                setWaitingForVerification(true)
                await requestVerificationMutation.mutateAsync()
            }
        } catch (err) {
            console.error('Onboarding completion failed:', err)
            toast.error('There was an issue finishing setup.')
        }
    }

    const handleEmailVerifiedAndFinish = async () => {
        try {
            const isVerified = await checkVerificationMutation.mutateAsync()
            if (!isVerified) {
                toast.warning('Email not verified yet — check your inbox.')
                return
            }
            toast.success('Email verified ✅')

            if (Object.keys(data).length > 0) {
                const accountSettings: any = {}
                if (data.geographic) accountSettings.geographic = data.geographic
                if (data.delivery) accountSettings.delivery_schedule = data.delivery
                if (data.timezone) accountSettings.timezone = data.timezone
                if (data.auto_send_enabled !== undefined) accountSettings.auto_send_enabled = data.auto_send_enabled
                if (data.email_delivery_enabled !== undefined) accountSettings.email_delivery_enabled = data.email_delivery_enabled
                if (Object.keys(accountSettings).length > 0) {
                    await saveSettingsMutation.mutateAsync(accountSettings)
                }
            }

            await users.completeOnboarding()
            await users.sendWelcomeEmail()
            await refreshUser()
            setWaitingForVerification(false)
            navigate('/app/dashboard', { replace: true })
        } catch (err) {
            console.error('Error finalizing after verification:', err)
            toast.error('Something went wrong finalizing your setup.')
        }
    }

    const stepMeta = STEPS[step - 1]

    return (
        <div className="ob-shell">
            <div className="ob-card" data-step={step}>

                {/* Topbar */}
                <div className="ob-topbar">
                    <img src="/images/FULL_LOGO_WHITE.svg" alt="Digestr" className="ob-logo" />
                    <DotStrip current={step} />
                    <button className="ob-skip" onClick={() => navigate('/app/dashboard', { replace: true })}>
                        skip →
                    </button>
                </div>

                {/* Step content — each step owns its own header + body + footer */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>

                    {step === 1 && (
                        <WelcomeLocationStep
                            eyebrow={stepMeta.eyebrow}
                            accentClass={stepMeta.accentClass}
                            btnClass={stepMeta.btnClass}
                            initialData={data.geographic}
                            onNext={(geographic) => { updateData({ geographic }); setStep(2) }}
                        />
                    )}

                    {step === 2 && (
                        <SourceSelectionStep
                            eyebrow={stepMeta.eyebrow}
                            accentClass={stepMeta.accentClass}
                            btnClass={stepMeta.btnClass}
                            onNext={() => setStep(3)}
                            onPrev={() => setStep(1)}
                        />
                    )}

                    {step === 3 && (
                        <PolariStep
                            eyebrow={stepMeta.eyebrow}
                            accentClass={stepMeta.accentClass}
                            btnClass={stepMeta.btnClass}
                            onNext={() => setStep(4)}
                            onBack={() => setStep(2)}
                        />
                    )}

                    {step === 4 && (
                        <EchoStyleStep
                            eyebrow={stepMeta.eyebrow}
                            accentClass={stepMeta.accentClass}
                            btnClass={stepMeta.btnClass}
                            initialData={data.content as any}
                            onNext={(content: any) => { updateData({ content }); setStep(5) }}
                            onBack={() => setStep(3)}
                        />
                    )}

                    {step === 5 && (
                        <DeliveryScheduleStep
                            eyebrow={stepMeta.eyebrow}
                            accentClass={stepMeta.accentClass}
                            btnClass={stepMeta.btnClass}
                            initialData={data.delivery}
                            onNext={(deliveryData: any) => {
                                updateData({
                                    delivery: deliveryData.delivery,
                                    timezone: deliveryData.timezone,
                                    auto_send_enabled: deliveryData.auto_send_enabled,
                                    email_delivery_enabled: deliveryData.email_delivery_enabled,
                                })
                                handleCompleteWizard()
                            }}
                            onBack={() => setStep(4)}
                        />
                    )}

                </div>

                {/* Email verification gate (post-wizard) */}
                {waitingForVerification && (
                    <div style={{
                        borderTop: '0.5px solid var(--color-border-tertiary)',
                        padding: '16px 20px',
                        background: 'rgba(102,0,255,0.03)',
                    }}>
                        <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)', marginBottom: 4 }}>
                            Verify your email to finish setup
                        </p>
                        <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 12, lineHeight: 1.55 }}>
                            We sent a link to your inbox. Click it, then confirm here.
                        </p>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button
                                className="ob-btn-back"
                                onClick={() => requestVerificationMutation.mutate()}
                                disabled={requestVerificationMutation.isPending}
                            >
                                {requestVerificationMutation.isPending ? 'Resending…' : 'Resend email'}
                            </button>
                            <button
                                className="ob-btn-next ob-btn-next--default"
                                onClick={handleEmailVerifiedAndFinish}
                                disabled={checkVerificationMutation.isPending || saveSettingsMutation.isPending}
                            >
                                {checkVerificationMutation.isPending || saveSettingsMutation.isPending
                                    ? 'Finishing…'
                                    : 'I verified my email'
                                }
                                <ArrowRight size={13} />
                            </button>
                        </div>
                    </div>
                )}

            </div>
        </div>
    )
}